/**
 * Admin axios client.
 *
 * Reuses the project-wide `httpClient` from `@/services/httpClient` for
 * baseURL/auth/refresh, and layers on:
 *  - typed `request<T>()` helper that returns `T` not AxiosResponse;
 *  - a `normalizeError(err)` utility that produces a stable shape:
 *    `{ status, code, message, details, requestId? }`.
 *
 * Do not export raw axios from this module — admin call sites should use
 * `request<T>` so that error handling stays consistent.
 */
import type { AxiosError, AxiosRequestConfig } from 'axios';
import httpClient from '@/services/httpClient';

export interface NormalizedError {
    status: number;
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
}

export const isNormalizedError = (e: unknown): e is NormalizedError =>
    typeof e === 'object' && e !== null
    && 'status' in e && 'code' in e && 'message' in e;

const inferCode = (status: number): string => {
    if (status === 401) return 'unauthenticated';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'not_found';
    if (status === 409) return 'conflict';
    if (status === 422) return 'validation';
    if (status >= 500) return 'server_error';
    return 'http_error';
};

export const normalizeError = (err: unknown): NormalizedError => {
    const axiosErr = err as AxiosError<{ detail?: string; code?: string; errors?: unknown }>;
    const status = axiosErr.response?.status ?? 0;
    const data = axiosErr.response?.data ?? {};
    const requestId = (axiosErr.response?.headers as Record<string, string> | undefined)?.['x-request-id'];
    return {
        status,
        code: (data as { code?: string }).code ?? inferCode(status),
        message: (data as { detail?: string }).detail ?? axiosErr.message ?? 'Request failed',
        details: (data as { errors?: unknown }).errors,
        requestId,
    };
};

export async function request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
        const resp = await httpClient.request<T>(config);
        return resp.data;
    } catch (e) {
        throw normalizeError(e);
    }
}
